import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, MapLayerMouseEvent, MapRef, Marker, Source } from 'react-map-gl/maplibre';
import { useTranslation } from '../../i18n/i18n';
import { formatTimeSeconds } from '../../utils/format';
import { bearingDeg, bounds } from '../../utils/geo';
import { GeoSample } from '../../utils/payload';
import { FitIcon, MinusIcon, PlusIcon } from '../icons/Icons';
import './RouteMap.scss';

// OpenStreetMap vector basemap without an API key; attribution is embedded in the style.
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
const FIT_PADDING = 56;
const MAX_FIT_ZOOM = 17;
const ARROW_COUNT = 5;
const HIT_LAYER = 'route-points-hit';

interface RouteMapProps {
  samples: GeoSample[];
  selected: number | null;
  onSelect: (index: number | null) => void;
}

const RouteMap = ({ samples, selected, onSelect }: RouteMapProps) => {
  const { t } = useTranslation('geo');
  const mapRef = useRef<MapRef>(null);
  const [hovering, setHovering] = useState(false);
  const n = samples.length;

  const routeGeoJson = useMemo(
    () => ({
      type: 'Feature' as const,
      properties: {},
      geometry: { type: 'LineString' as const, coordinates: samples.map((s) => [s.longitude, s.latitude]) },
    }),
    [samples],
  );

  // Start and end have their own markers, so only the points in between are drawn as dots.
  const pointsGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: samples.map((s, i) => ({
        type: 'Feature' as const,
        properties: { i, inner: i > 0 && i < n - 1 },
        geometry: { type: 'Point' as const, coordinates: [s.longitude, s.latitude] },
      })),
    }),
    [samples, n],
  );

  const arrows = useMemo(() => {
    if (n < 3) return [];
    const picks = Array.from({ length: ARROW_COUNT }, (_, k) => Math.floor(((k + 0.5) * (n - 1)) / ARROW_COUNT));
    return Array.from(new Set(picks))
      .filter(
        (i) => samples[i].latitude !== samples[i + 1].latitude || samples[i].longitude !== samples[i + 1].longitude,
      )
      .map((i) => ({
        i,
        longitude: (samples[i].longitude + samples[i + 1].longitude) / 2,
        latitude: (samples[i].latitude + samples[i + 1].latitude) / 2,
        rotation: bearingDeg(samples[i], samples[i + 1]),
      }));
  }, [samples, n]);

  const fit = useCallback(
    (duration = 600) => {
      if (!mapRef.current || !n) return;
      mapRef.current.fitBounds(bounds(samples), { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM, duration });
    },
    [samples, n],
  );

  useEffect(() => fit(0), [fit]);

  const onMouseMove = (e: MapLayerMouseEvent) => {
    const feature = e.features?.[0];
    setHovering(!!feature);
    if (feature) {
      const i = Number(feature.properties?.i);
      if (i !== selected) onSelect(i);
    }
  };

  const sel = selected !== null && selected < n ? samples[selected] : undefined;

  return (
    <div
      className={`RouteMap${hovering ? ' RouteMap--pointer' : ''}`}
      onMouseLeave={() => {
        setHovering(false);
        onSelect(null);
      }}
    >
      <Map
        ref={mapRef}
        mapStyle={MAP_STYLE}
        initialViewState={
          n ? { bounds: bounds(samples), fitBoundsOptions: { padding: FIT_PADDING, maxZoom: MAX_FIT_ZOOM } } : undefined
        }
        dragRotate={false}
        touchPitch={false}
        pitchWithRotate={false}
        interactiveLayerIds={[HIT_LAYER]}
        onMouseMove={onMouseMove}
        onClick={onMouseMove}
        onLoad={() => fit(0)}
        style={{ position: 'absolute', inset: 0 }}
      >
        <Source id="route" type="geojson" data={routeGeoJson}>
          <Layer
            id="route-halo"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': '#FFFFFF', 'line-width': 10 }}
          />
          <Layer
            id="route-line"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': '#1F6FD1', 'line-width': 5 }}
          />
        </Source>
        <Source id="route-points" type="geojson" data={pointsGeoJson}>
          <Layer
            id="route-points-dot"
            type="circle"
            filter={['all', ['==', ['get', 'inner'], true], ['!=', ['get', 'i'], selected ?? -1]]}
            paint={{
              'circle-radius': 4,
              'circle-color': '#1F6FD1',
              'circle-stroke-color': '#FFFFFF',
              'circle-stroke-width': 2,
            }}
          />
          <Layer
            id="route-points-selected"
            type="circle"
            filter={['all', ['==', ['get', 'inner'], true], ['==', ['get', 'i'], selected ?? -1]]}
            paint={{
              'circle-radius': 8,
              'circle-color': '#05070B',
              'circle-stroke-color': '#FFFFFF',
              'circle-stroke-width': 2,
            }}
          />
          <Layer id={HIT_LAYER} type="circle" paint={{ 'circle-radius': 14, 'circle-opacity': 0 }} />
        </Source>

        {arrows.map((a) => (
          <Marker
            key={a.i}
            longitude={a.longitude}
            latitude={a.latitude}
            rotation={a.rotation}
            rotationAlignment="map"
            style={{ pointerEvents: 'none' }}
          >
            <svg width="20" height="20" viewBox="-10 -10 20 20" aria-hidden="true">
              <path
                d="M-6 4 L0 -6 L6 4"
                fill="none"
                stroke="#1F6FD1"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M-6 4 L0 -6 L6 4"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Marker>
        ))}

        {n > 0 && (
          <Marker longitude={samples[0].longitude} latitude={samples[0].latitude} style={{ pointerEvents: 'none' }}>
            <span className="RouteMap__start" />
          </Marker>
        )}
        {n > 1 && (
          <Marker
            longitude={samples[n - 1].longitude}
            latitude={samples[n - 1].latitude}
            style={{ pointerEvents: 'none' }}
          >
            <span className="RouteMap__end" />
          </Marker>
        )}

        {sel && (
          <Marker
            longitude={sel.longitude}
            latitude={sel.latitude}
            anchor="bottom"
            offset={[0, -16]}
            style={{ pointerEvents: 'none', zIndex: 2 }}
          >
            <div className="RouteMap__tip">
              <span>{t('route.point', { time: formatTimeSeconds(sel.time), n: (selected as number) + 1 })}</span>
              <strong>
                {t('route.speed', { speed: sel.speed ?? '—', angle: sel.angle === undefined ? '—' : `${sel.angle}°` })}
              </strong>
              <code>
                {sel.latitude.toFixed(6)}, {sel.longitude.toFixed(6)}
              </code>
            </div>
          </Marker>
        )}
      </Map>

      <div className="RouteMap__controls">
        <button
          type="button"
          aria-label={t('route.zoomIn')}
          title={t('route.zoomIn')}
          onClick={() => mapRef.current?.zoomIn()}
        >
          <PlusIcon />
        </button>
        <span aria-hidden="true" />
        <button
          type="button"
          aria-label={t('route.zoomOut')}
          title={t('route.zoomOut')}
          onClick={() => mapRef.current?.zoomOut()}
        >
          <MinusIcon />
        </button>
        <span aria-hidden="true" />
        <button type="button" aria-label={t('route.fit')} title={t('route.fit')} onClick={() => fit()}>
          <FitIcon />
        </button>
      </div>
    </div>
  );
};

export default RouteMap;
